/**
 * tokenService.ts
 * OH3 トークン（ポイント）システム フロント側ユーティリティ
 *
 * 有料機能の実行前に consumeToken() を呼び、ポイント消費＆残高チェックを行う。
 *
 * ポイント不足の場合:
 * - store.state.tokenLimitExceeded にエラー情報をセット → App.vueでダイアログ表示
 * - 呼び出し元が await していなくてもユーザーに通知される
 */

import { phpApiPost, phpApiGet } from '@/js/phpApi'
import store from '@/store'
import { warnFutureLogin } from '@/js/utils/messageHelper'

/**
 * トークン制限の強制フラグ
 * true  → 非ログイン・ポイント不足時に機能をブロック（本番モード）
 * false → 従来通り記録のみ、機能はブロックしない（テストモード）
 */
export const ENFORCE_TOKEN_LIMIT = true

/**
 * ログイン必須ゲート
 * true  → 非ログインユーザーは有償機能をブロック（4/1～有効化）
 * false → 従来通り非ログインでも利用可能
 */
export const REQUIRE_LOGIN_FOR_FEATURES = true

// 保存・エクスポート系（非ログインユーザーに今後ログイン専用になる旨を警告）
const SAVE_FEATURES = new Set([
  'fieldnote_save', 'mydraw_save', 'photomemo_save', 'snapshot_save',
  'geojson_create', 'export_pdf', 'export_kml', 'export_csv',
  'export_geojson', 'import_kml', 'import_kmz',
  'export_sima', 'export_dxf', 'export_shape', 'export_kyusekihyo',
  'import_sima', 'import_dxf', 'import_image',
  'georef_save', 'pmtiles_convert', 'draw_photo_upload',
])

// レスポンス型定義
export interface ConsumeResult {
  success: boolean
  error?: string
  feature_name?: string
  token_cost?: number
  point_balance?: number
  is_unlimited?: boolean
  org_point_balance?: number | null
}

export interface BalanceResult {
  success: boolean
  error?: string
  point_balance?: number
  is_unlimited?: boolean
  is_org_member?: boolean
  organization_id?: number | null
  org_plan_id?: string | null
  org_point_balance?: number | null
  plan_type?: string
  subscription_end_date?: string | null
}

// 残高キャッシュ（連続呼び出し対策）
let balanceCache: BalanceResult | null = null
let balanceCacheTime = 0
const CACHE_TTL = 5000 // 5秒

/**
 * トークンを消費する（機能実行前に呼ぶ）
 *
 * @param featureKey - 機能識別キー（例: 'ai_bukken_report'）
 * @returns ConsumeResult
 *
 * POINT_INSUFFICIENT の場合:
 * - store.state.tokenLimitExceeded に情報をセット
 * - App.vue のダイアログが自動表示される
 */
export async function consumeToken(featureKey: string): Promise<ConsumeResult> {
  // 未ログインでも 'anonymous' として記録（利用実態の計測用）
  const userId = store.state.userId || 'anonymous'

  // === ログイン必須ゲート: 非ログインユーザーをブロック ===
  if (REQUIRE_LOGIN_FOR_FEATURES && userId === 'anonymous') {
    const st = store.state as any
    st.tokenLoginRequired = true  // App.vueでログイン要求ダイアログ表示
    // throwで呼び出し元のcatchに落とし、処理を確実に中断
    throw new Error('LOGIN_REQUIRED')
  }

  // === 強制モード: ポイント不足時に機能をブロック ===
  if (ENFORCE_TOKEN_LIMIT && userId === 'anonymous') {
    const st = store.state as any
    st.tokenLoginRequired = true
    throw new Error('LOGIN_REQUIRED')
  }

  // ニックネーム（参考情報としてDBに記録）
  const nickname = store.state.myNickname || localStorage.getItem('lastNickname') || ''

  try {
    const result = await phpApiPost<ConsumeResult>('tokenApi.php', {
      action: 'consume',
      userId,
      featureKey,
      nickname
    })

    // キャッシュを無効化（残高が変わったので）
    balanceCache = null

    if (result.success) {
      // storeのトークン残高をリアルタイム更新（ログインユーザーのみ）
      if (userId !== 'anonymous') {
        const st = store.state as any
        st.tokenBalance = {
          success: true,
          point_balance: result.point_balance,
          is_unlimited: result.is_unlimited,
          org_point_balance: result.org_point_balance,
          plan_type: st.tokenBalance?.plan_type || 'free',
        } as BalanceResult
      }
    } else if (result.error === 'POINT_INSUFFICIENT' || result.error === 'TOKEN_LIMIT_EXCEEDED') {
      // ポイント不足 → storeに情報セットしてダイアログ表示
      console.warn(`[TokenService] ${featureKey}: ポイント不足`)
      const st = store.state as any
      st.tokenLimitExceeded = {
        featureKey,
        tokenCost: result.token_cost,
        pointBalance: result.point_balance,
      }
    } else {
      console.warn(`[TokenService] ${featureKey}: 消費失敗 - ${result.error}`)
    }

    return result
  } catch (error) {
    // ネットワークエラー等 → 機能を止めない（計測が目的のうちは安全側に倒す）
    console.warn('[TokenService] consumeToken failed:', error)
    return { success: true, error: 'NETWORK_ERROR_IGNORED' }
  }
}

/**
 * トークン残高を取得する
 *
 * @returns BalanceResult
 */
export async function getTokenBalance(): Promise<BalanceResult> {
  const userId = store.state.userId
  if (!userId) {
    return { success: false, error: 'NOT_LOGGED_IN' }
  }

  // キャッシュチェック
  if (balanceCache && (Date.now() - balanceCacheTime) < CACHE_TTL) {
    return balanceCache
  }

  try {
    const result = await phpApiGet<BalanceResult>('tokenApi.php', {
      action: 'balance',
      userId
    })

    if (result.success) {
      balanceCache = result
      balanceCacheTime = Date.now()
      // storeのトークン残高をリアルタイム更新
      ;(store.state as any).tokenBalance = result
    }

    return result
  } catch (error) {
    console.warn('[TokenService] getTokenBalance failed:', error)
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

/**
 * 残高キャッシュをクリアする
 * （手動リフレッシュ時に呼ぶ）
 */
export function clearBalanceCache(): void {
  balanceCache = null
  balanceCacheTime = 0
}

// 月間使用量レスポンス型
export interface MonthlyUsageResult {
  success: boolean
  error?: string
  days?: number
  total_cost?: number
  total_count?: number
}

// 月間使用量キャッシュ（バナー表示のたびに重いクエリを避ける）
let monthlyUsageCache: MonthlyUsageResult | null = null
let monthlyUsageCacheTime = 0
const MONTHLY_USAGE_CACHE_TTL = 5 * 60 * 1000 // 5分

/**
 * 過去N日間のポイント消費量を取得する（体験プランバナー用）
 *
 * @param days - 集計期間（デフォルト30日）
 * @returns MonthlyUsageResult
 */
export async function getMonthlyUsage(days = 30): Promise<MonthlyUsageResult> {
  const userId = store.state.userId
  if (!userId) {
    return { success: false, error: 'NOT_LOGGED_IN' }
  }

  // キャッシュチェック
  if (monthlyUsageCache && monthlyUsageCache.days === days
      && (Date.now() - monthlyUsageCacheTime) < MONTHLY_USAGE_CACHE_TTL) {
    return monthlyUsageCache
  }

  try {
    const result = await phpApiGet<MonthlyUsageResult>('tokenApi.php', {
      action: 'monthly_usage',
      userId,
      days: String(days),
    })

    if (result.success) {
      monthlyUsageCache = result
      monthlyUsageCacheTime = Date.now()
    }
    return result
  } catch (error) {
    console.warn('[TokenService] getMonthlyUsage failed:', error)
    return { success: false, error: 'NETWORK_ERROR' }
  }
}

/**
 * 月間使用量キャッシュをクリア
 */
export function clearMonthlyUsageCache(): void {
  monthlyUsageCache = null
  monthlyUsageCacheTime = 0
}
